export type MailRecipients = string | string[]

export type MailMessage = {
  subject: string
  text: string
  html: string
}

export type MailQueuePayload = {
  to: MailRecipients
  cc?: MailRecipients
  bcc?: MailRecipients
  from?: string
  message: MailMessage
  memo?: string
}

type BuildTextMailParams = {
  to: MailRecipients
  subject: string
  text: string
  memo?: string
  cc?: MailRecipients
  bcc?: MailRecipients
  from?: string
}

type MailBaseParams = {
  to: string
  memo?: string
}

type MailGreetingParams = {
  schoolName?: string
  contactName?: string
}

type PendingSchoolRegistrationMailParams = MailBaseParams &
  Required<Pick<MailGreetingParams, 'contactName' | 'schoolName'>> & {
    appliedAtText: string
  }

type SchoolApprovalMailParams = MailBaseParams &
  MailGreetingParams & {
  temporaryPassword: string
}

type PasswordResetMailParams = MailBaseParams & {
  resetLink: string
}

type ContactCreatedMailParams = MailBaseParams &
  Required<Pick<MailGreetingParams, 'schoolName'>> &
  Pick<MailGreetingParams, 'contactName'> & {
  subject: string
  content: string
}

type ContactCompletedMailParams = MailBaseParams &
  MailGreetingParams & {
  subject: string
}

type ContactAdminReplyMailParams = MailBaseParams &
  MailGreetingParams & {
  subject: string
  content: string
}

type IrogamiRequestCompletedMailParams = MailBaseParams &
  MailGreetingParams & {
  projectName?: string
  projectNumber?: string | null
  requestId: string
  requestedCount: number
}

type IrogamiRequestCancelledMailParams = MailBaseParams &
  MailGreetingParams & {
  projectName?: string
  projectNumber?: string | null
  requestId: string
  requestedCount: number
}

type ProjectCreatedMailParams = MailBaseParams &
  MailGreetingParams & {
  projectName?: string
  startDateText?: string
  endDateText?: string
  studentGrade?: string
  projectContactPersonName?: string
  registeredAtText: string
}

type PostcardShippingMarkedCompleteMailParams = MailBaseParams &
  MailGreetingParams & {
  projectName: string
  projectNumber?: string | null
}

type CollectionClosedMailParams = MailBaseParams &
  MailGreetingParams & {
  projectName: string
  projectNumber?: string | null
}

type ProjectEndProcedureDueMailParams = MailBaseParams &
  MailGreetingParams & {
  projectName: string
  projectNumber?: string | null
}

type ProjectCaseCompletedMailParams = MailBaseParams &
  MailGreetingParams & {
  projectName: string
  projectNumber?: string | null
}

const MAIL_COMMON_FOOTER = [
  '───────────────────────────────────',
  '職場体験準備・管理システム｜イロガミ',
  'https://irogami-taiken.com',
  '',
  'ログインはこちら',
  'https://school.irogami.app',
  '',
  '＜ご利用校様専用窓口＞',
  '0120-1653-16（平日 11:00～17:00）',
  '',
  '＜運営会社＞',
  'イロガミ株式会社',
  '〒150-0001　東京都渋谷区神宮前6-19-16 越一ビル3F',
  '───────────────────────────────────',
].join('\n')

const buildMailGreetingLines = (params: MailGreetingParams): string[] => {
  const schoolName = (params.schoolName ?? '').trim()
  const contactName = (params.contactName ?? '').trim()

  if (schoolName && contactName) {
    return [schoolName, `${contactName}様`]
  }
  if (contactName) {
    return [`${contactName}様`]
  }
  if (schoolName) {
    return [`${schoolName}様`]
  }
  return []
}

const displayOrUnspecified = (value?: string | null): string => {
  const normalized = (value ?? '').trim()
  return normalized || '（未設定）'
}

const buildProjectScheduleText = (params: {
  startDateText?: string
  endDateText?: string
}): string => {
  const start = (params.startDateText ?? '').trim()
  const end = (params.endDateText ?? '').trim()
  if (start && end) return `${start}〜${end}`
  if (start) return start
  if (end) return end
  return '（未設定）'
}

export class MailService {
  static withCommonFooter(text: string): string {
    return `${text}\n\n${MAIL_COMMON_FOOTER}`
  }

  static htmlFromText(text: string): string {
    return text.replace(/\n/g, '<br>')
  }

  static fromText(params: BuildTextMailParams): MailQueuePayload {
    const textWithFooter = MailService.withCommonFooter(params.text)

    return {
      to: params.to,
      ...(params.cc ? { cc: params.cc } : {}),
      ...(params.bcc ? { bcc: params.bcc } : {}),
      ...(params.from ? { from: params.from } : {}),
      message: {
        subject: params.subject,
        text: textWithFooter,
        html: MailService.htmlFromText(textWithFooter),
      },
      ...(params.memo ? { memo: params.memo } : {}),
    }
  }

  static pendingSchoolRegistrationReceived(params: PendingSchoolRegistrationMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'この度は「イロガミ」へご登録いただき、誠にありがとうございます。',
      '仮登録が完了いたしました。',
      '',
      `学校名：${params.schoolName.trim()}`,
      `申請日時：${params.appliedAtText}`,
      '規約同意：利用規約およびプライバシーポリシーに同意済み',
      '',
      '【本登録について】',
      'サポートチームよりお電話で登録内容を確認させていただきます。',
      '確認後、本登録となりアカウントが開設されますので、今しばらくお待ちください。',
      '',
      '※本メールは自動送信メールです。',
      '本メールにお心当たりのない場合は、お手数ですが下記サイトのお問い合わせフォームよりご連絡ください。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】仮登録が完了しました',
      text,
      memo: params.memo ?? 'pending_school_registration_received',
    })
  }

  static schoolAccountApproved(params: SchoolApprovalMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'この度は「イロガミ」へご登録いただき、誠にありがとうございます。',
      '本登録が完了し、アカウントが開設されました。',
      '',
      '【初回ログインについて】',
      '下記の仮パスワードでログインし、パスワードの変更をお願いいたします。',
      '',
      `仮パスワード：${params.temporaryPassword}`,
      '',
      '※本メールは自動送信メールです。',
      '本メールにお心当たりのない場合は、お手数ですが下記サイトのお問い合わせフォームよりご連絡ください。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】本登録が完了しました',
      text,
      memo: params.memo ?? 'school_account_approved',
    })
  }

  static passwordReset(params: PasswordResetMailParams): MailQueuePayload {
    const text = [
      'パスワード再設定のリクエストを受け付けました。',
      '',
      '以下のリンクをクリックして、パスワードを再設定してください。',
      '',
      params.resetLink,
      '',
      '※このリンクは有効期限があります。心当たりがない場合はこのメールを破棄してください。',
      '',
      '※本メールは自動送信です。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験】パスワード再設定のご案内',
      text,
      memo: params.memo ?? 'password_reset',
    })
  }

  static contactCreated(params: ContactCreatedMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'お問い合わせを以下のとおり受け付けました。',
      '',
      '【お問い合わせ内容】',
      `件名：${params.subject}`,
      `内容：${params.content}`,
      '',
      '返信はシステム内にてお送りいたします。',
      '恐れ入りますが、今しばらくお待ちくださいますようお願いいたします。',
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】お問い合わせを受け付けました',
      text,
      memo: params.memo ?? 'contact_created',
    })
  }

  static contactCompleted(params: ContactCompletedMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      '以下のお問い合わせは完了しました。',
      'ご不明点などがございましたら、改めてお問い合わせ画面よりご連絡ください。',
      '',
      '【お問い合わせ】',
      `件名：${params.subject}`,
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】お問い合わせが完了しました',
      text,
      memo: params.memo ?? 'contact_status_completed',
    })
  }

  static contactAdminReply(params: ContactAdminReplyMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'お問い合わせにイロガミから返信がありました。',
      '',
      '【お問い合わせ】',
      `件名：${params.subject}`,
      `返信内容：${params.content}`,
      '',
      '詳細はシステム内のお問い合わせ画面でご確認ください。',
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】お問い合わせに返信がありました',
      text,
      memo: params.memo ?? 'contact_admin_reply',
    })
  }

  static irogamiRequestCompleted(params: IrogamiRequestCompletedMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'いつもイロガミをご利用いただき、誠にありがとうございます。',
      '事業所データの生成が完了いたしました。',
      '',
      '生成されたデータは、システムにログインのうえ、',
      '本案件画面の「事業所データ」タブよりご確認いただけます。',
      '',
      '■確認～はがき郵送までの流れ',
      '　1. 生成された事業所データをご確認ください。',
      '　　※登録元が「生成」、かつ郵送状況が「郵送前」のものが対象です。',
      '',
      '　2. 郵送しない場合は、郵送設定を「見送り」に変更してください。',
      '　　※変更分は学校側での登録が必要です。',
      '',
      '　3. データがそろい次第、「郵送開始」より手続きを進めてください。',
      '',
      'ご不明点がございましたら、お気軽にお問い合わせください。',
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】事業所データの生成が完了しました',
      text,
      memo: params.memo ?? 'irogami_request_completed',
    })
  }

  static irogamiRequestCancelled(params: IrogamiRequestCancelledMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'イロガミへの依頼について、キャンセルとなりました。',
      '',
      '【依頼内容】',
      `案件名：${params.projectName ?? '（未設定）'}`,
      `案件番号：${params.projectNumber ?? '（未設定）'}`,
      `依頼ID：${params.requestId}`,
      `依頼件数：${params.requestedCount}件`,
      '',
      'ご不明点がある場合は、管理画面よりお問い合わせください。',
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】依頼がキャンセルされました',
      text,
      memo: params.memo ?? 'irogami_request_cancelled',
    })
  }

  static postcardShippingMarkedComplete(params: PostcardShippingMarkedCompleteMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'いつもイロガミをご利用いただき、誠にありがとうございます。',
      '',
      'はがきの郵送準備が完了いたしました。',
      '今後、各事業所様へ依頼はがきが順次届きます。',
      '',
      'はがきが届いた事業所様は、はがきに記載のQRコードまたはURLより、受け入れ可否をご回答いただけます。',
      '',
      'なお、事業所様からの回答は、はがき到着後に内容を確認・検討したうえで行われるため、',
      'すぐに回答が得られない場合がございます。あらかじめご了承ください。',
      '',
      '■回答状況の確認方法',
      '案件詳細画面の「事業所回答一覧」タブより、回答状況・内容をご確認いただけます。',
      '',
      '■回答受付期限について',
      '設定された回答期限を過ぎると、自動的に事業所様からの回答受付は終了いたします。',
      '回答期限より前に受付を終了する場合は、「回答受付停止」ボタンよりお手続きください。',
      'また、回答期限の変更や、受付終了後の回答受付の再開も可能です。',
      '',
      '■追加のはがき郵送について',
      '必要に応じて、追加で事業所データを登録し、新たにはがきを郵送することが可能です。',
      '',
      '詳しい操作手順はマニュアルをご参照ください。',
      '',
      'ご不明点がございましたら、お気軽にお問い合わせください。',
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】はがき郵送準備完了・回答受付開始のお知らせ',
      text,
      memo: params.memo ?? 'postcard_shipping_marked_complete',
    })
  }

  static collectionClosed(params: CollectionClosedMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'いつもイロガミをご利用いただき、誠にありがとうございます。',
      '',
      '事業所様からの回答受付が終了いたしました。',
      '',
      '■回答状況の確認方法',
      '案件詳細画面の「事業所回答一覧」タブより、回答状況・内容をご確認いただけます。',
      '',
      '■回答受付の再開方法',
      '回答受付を再開する場合は、「回答受付再開」ボタンよりお手続きください。',
      '',
      '■追加のはがき郵送について',
      '必要に応じて、追加で事業所データを登録し、新たにはがきを郵送することが可能です。',
      '',
      '■職場体験の終了手続き',
      '職場体験の最終日終了後、「職場体験終了」ボタンより手続きを行ってください。',
      '※終了手続き後は、事業所データや回答一覧の編集はできません。',
      '　案件詳細ページでは、閲覧および回答一覧データの出力のみ可能です。',
      '※請求書は、終了手続き後に発行されます。',
      '',
      '詳しい操作手順はマニュアルをご参照ください。',
      'ご不明点がございましたら、お気軽にお問い合わせください。',
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】回答受付終了のお知らせ',
      text,
      memo: params.memo ?? 'collection_closed',
    })
  }

  static projectEndProcedureDue(params: ProjectEndProcedureDueMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'いつもイロガミをご利用いただき、誠にありがとうございます。',
      '',
      '本メールは、職場体験の実施最終日に自動でお送りしているご案内です。',
      '以下の職場体験について、終了手続きをお願いいたします。',
      '',
      `案件番号：${displayOrUnspecified(params.projectNumber)}`,
      `職場体験名：${displayOrUnspecified(params.projectName)}`,
      '',
      '■職場体験の終了手続き',
      '案件詳細ページ内「職場体験終了」ボタンより手続きを行ってください。',
      '',
      '※終了手続き後は、案件に関する情報の編集はできません。',
      '　案件詳細ページでは、各種情報の閲覧および回答一覧データの出力が可能です。',
      '',
      '■請求書の発行について',
      '終了手続き完了後、当日〜翌営業日を目安に請求書が発行され、',
      'マネーフォワードよりご登録のメールアドレス宛に送付されます。',
      '',
      '万が一メールが確認できない場合は、',
      '迷惑メールフォルダに振り分けられている可能性がございますので、併せてご確認ください。',
      '',
      'ご不明点がございましたら、お気軽にお問い合わせください。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】職場体験終了後のご案内',
      text,
      memo: params.memo ?? 'project_end_procedure_due',
    })
  }

  static projectCaseCompleted(params: ProjectCaseCompletedMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'いつもイロガミをご利用いただき、誠にありがとうございます。',
      '職場体験の終了手続きが完了いたしました。',
      '',
      `案件番号：${displayOrUnspecified(params.projectNumber)}`,
      `職場体験名：${displayOrUnspecified(params.projectName)}`,
      '',
      '■終了後の操作について',
      '案件詳細ページでは、各種情報の閲覧および回答一覧データの出力が可能です。',
      'なお、案件に関する情報の編集はできません。',
      '',
      '■請求書の発行について',
      '終了手続き完了後、当日〜翌営業日を目安に発行され、',
      'マネーフォワードよりご登録のメールアドレス宛に送付されます。',
      '',
      '万が一メールが確認できない場合は、',
      '迷惑メールフォルダに振り分けられている可能性がございますので、併せてご確認ください。',
      '',
      '■アンケートご協力のお願い',
      '今後のサービス向上のため、よろしければ簡単なアンケートにご協力いただけますと幸いです。',
      '（回答時間の目安：約2〜3分）',
      '',
      '【アンケートはこちら】',
      'https://forms.gle/kSBTYU9f8SRoV2vs9',
      '',
      'ご不明点がございましたら、お気軽にお問い合わせください。',
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】終了手続き完了のお知らせ',
      text,
      memo: params.memo ?? 'project_case_completed',
    })
  }

  static projectCreated(params: ProjectCreatedMailParams): MailQueuePayload {
    const text = [
      ...buildMailGreetingLines({
        schoolName: params.schoolName,
        contactName: params.contactName,
      }),
      '',
      'この度は「イロガミ」をご利用いただき、誠にありがとうございます。',
      '案件の登録が完了いたしました。',
      '',
      '【登録内容】',
      `職場体験名：${displayOrUnspecified(params.projectName)}`,
      `実施期間：${buildProjectScheduleText({
        startDateText: params.startDateText,
        endDateText: params.endDateText,
      })}`,
      `対象学年：${displayOrUnspecified(params.studentGrade)}`,
      `担当者：${displayOrUnspecified(params.projectContactPersonName)}`,
      `案件登録日：${params.registeredAtText.trim() || '（未設定）'}`,
      '',
      '詳細はログイン後、メニュー「案件一覧」よりご確認いただけます。',
      '',
      'ご登録内容は、事業所様にお送りする「依頼はがき」および「受け入れ可否回答ページ」に反映されます。',
      '※依頼はがきの郵送開始後は、内容を変更できませんのでご注意ください。',
      '',
      '■ご利用料金とお支払いについて',
      '本件の登録に伴い、「システム利用料」が確定いたしました。',
      'また、「はがき郵送費」は、郵送したはがきの枚数に応じて加算されます。',
      '',
      'ご請求は、本案件の完了後に一括して行います。',
      '現在のご利用状況は、案件詳細画面の「請求」タブよりご確認いただけます。',
      '',
      '※本メールは自動送信メールです。',
    ].join('\n')

    return MailService.fromText({
      to: params.to.trim(),
      subject: '【職場体験｜イロガミ】職場体験の案件登録が完了しました',
      text,
      memo: params.memo ?? 'project_created',
    })
  }
}
